import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Calendar, Clock, User, Phone, MapPin, MessageSquare, Check, Shield, Building2, DollarSign } from 'lucide-react';
import { createBooking } from '../utils/api';
import SEOHead from '../components/SEOHead';

const SunMedBooking = () => {
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [accessError, setAccessError] = useState('');
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top on mount and whenever the visible section changes.
  // useLayoutEffect runs before paint so the Sun Med section always opens
  // at the top, even when the user navigated here from a scrolled page.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [accessGranted, bookingConfirmed]);

  const [formData, setFormData] = useState({
    patientName: '',
    patientPhone: '',
    pickupLocation: '',
    appointmentDate: '',
    appointmentTime: '',
    officeContact: '',
    specialInstructions: ''
  });

  const handleAccessCodeSubmit = (e) => {
    e.preventDefault();
    if (accessCode.toUpperCase() === 'SUNMED60') {
      setAccessGranted(true);
      setAccessError('');
    } else {
      setAccessError('Invalid access code');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Map Sun Med form data to standard booking format
      const bookingPayload = {
        passengerName: formData.patientName,
        passengerEmail: formData.patientPhone.replace(/\D/g, '') + '@sunmed-booking.taxi-98.com', // Use phone-based email for tracking
        passengerPhone: formData.patientPhone,
        pickupZone: 'Sun Med',
        dropoffZone: 'Medical Facility',
        pickupLocation: formData.pickupLocation,
        dropoffLocation: 'Bahamas Retina and Eye Care Services, Island Ln, Nassau, Bahamas',
        additionalStops: [],
        date: formData.appointmentDate,
        time: formData.appointmentTime,
        passengers: 1,
        bags: false,
        luggage: 0,
        largeBags: 0,
        childrenUnder3: 0,
        carSeats: 0,
        waitingMinutes: 60,
        specialRequests: `☎️ PATIENT CONTACT: ${formData.patientPhone}\n\n🏥 Sun Med Medical Transport\n\nOffice Contact: ${formData.officeContact}\n\nService Type: Medical Round-Trip\nClient Type: Business Partner\nRate Type: Fixed Contract Rate ($125.00)\n\nSpecial Instructions: ${formData.specialInstructions || 'None'}`,
        paymentMethod: 'Fixed Contract',
        estimatedFare: 125.00,
        hCaptchaToken: 'SUNMED_INTERNAL_BOOKING' // Bypass captcha for internal portal
      };

      const newBooking = await createBooking(bookingPayload);
      setBookingId(newBooking.id);
      setBookingConfirmed(true);
      setIsSubmitting(false);

    } catch (error) {
      console.error('Booking failed:', error);
      alert('Failed to create booking. Please try again or contact Taxi-98 support.');
      setIsSubmitting(false);
    }
  };

  const handleBookAnother = () => {
    setBookingConfirmed(false);
    setBookingId('');
    setFormData({
      patientName: '',
      patientPhone: '',
      pickupLocation: '',
      appointmentDate: '',
      appointmentTime: '',
      officeContact: '',
      specialInstructions: ''
    });
  };

  // Access Code Screen
  if (!accessGranted) {
    return (
      <>
        <SEOHead
          title="Sun Med Booking Portal - Private Access | Taxi-98"
          description="Private medical transport booking portal for authorized Sun Med office use only."
          canonical="/sun-med-booking"
        />
        
        <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 flex items-center justify-center px-4 py-20">
          <div className="max-w-md w-full">
            <div className="bg-white p-10 rounded-3xl shadow-2xl border border-cyan-100">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                  <Shield className="w-10 h-10 text-white" />
                </div>
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
                Sun Med Booking Portal
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Private medical transport request form for authorized office use.
              </p>

              <form onSubmit={handleAccessCodeSubmit} className="space-y-6">
                <div>
                  <label htmlFor="accessCode" className="block text-gray-700 font-medium mb-2">
                    Access Code
                  </label>
                  <input
                    type="password"
                    id="accessCode"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    required
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                    placeholder="Enter access code"
                  />
                  {accessError && (
                    <p className="text-red-600 text-sm mt-2 font-medium">{accessError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-4 rounded-lg font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  Access Portal
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-500 text-center">
                  Authorized Sun Med staff only. For assistance, contact Taxi-98 at <span className="font-medium text-cyan-600">+1-242-810-7335</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Success Screen
  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-12 rounded-3xl border border-green-200 text-center shadow-xl">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-400">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Booking Request Submitted Successfully</h2>
            <p className="text-xl text-gray-700 mb-6">
              Medical transport has been scheduled with Taxi-98.
            </p>
            <div className="bg-cyan-50 p-6 rounded-xl mb-8 border border-cyan-200">
              <p className="text-gray-600 mb-2">Confirmation Number:</p>
              <p className="text-2xl font-bold text-cyan-600">{bookingId}</p>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white p-6 rounded-xl mb-8">
              <p className="text-cyan-100 mb-2">Fixed Contract Rate</p>
              <p className="text-4xl font-bold">$125.00</p>
              <p className="text-sm text-cyan-100 mt-2">Round-trip medical transport with 1 hour waiting time</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                <Building2 className="w-5 h-5 text-blue-600 mr-2" />
                Service Details
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p><strong>Patient:</strong> {formData.patientName}</p>
                <p><strong>Pickup:</strong> {formData.pickupLocation}</p>
                <p><strong>Destination:</strong> Bahamas Retina and Eye Care Services</p>
                <p><strong>Date:</strong> {formData.appointmentDate}</p>
                <p><strong>Time:</strong> {formData.appointmentTime}</p>
                <p><strong>Contact:</strong> {formData.officeContact}</p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
              <p className="text-gray-700 text-sm">
                <strong className="text-gray-900">Next steps:</strong> Taxi-98 will contact Sun Med office to confirm the booking. The driver will arrive at the pickup location at the scheduled time. Return trip is included after the 1-hour appointment wait time.
              </p>
            </div>

            <button
              onClick={handleBookAnother}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-4 rounded-lg font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
            >
              Book Another Medical Transport
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Booking Form
  return (
    <>
      <SEOHead
        title="Sun Med Booking Portal | Taxi-98"
        description="Private medical transport booking portal for Sun Med office."
        canonical="/sun-med-booking"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold border border-blue-300 inline-block mb-4">
              🏥 Sun Med Medical Transport
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Sun Med Booking Portal
            </h1>
            <p className="text-xl text-gray-600">
              Private medical transport request form for authorized office use
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-cyan-100">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Patient Information */}
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <User className="w-6 h-6 text-cyan-600 mr-3" />
                      Patient Information
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label htmlFor="patientName" className="block text-gray-700 font-medium mb-2">
                          Patient Full Name *
                        </label>
                        <input
                          type="text"
                          id="patientName"
                          name="patientName"
                          value={formData.patientName}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                          placeholder="John Doe"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label htmlFor="patientPhone" className="block text-gray-700 font-medium mb-2">
                          Patient Phone Number *
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="tel"
                            id="patientPhone"
                            name="patientPhone"
                            value={formData.patientPhone}
                            onChange={handleChange}
                            required
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                            placeholder="1-242-XXX-XXXX"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trip Details */}
                  <div className="border-t border-cyan-100 pt-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <MapPin className="w-6 h-6 text-cyan-600 mr-3" />
                      Trip Details
                    </h2>
                    <div className="space-y-6">
                      <div>
                        <label htmlFor="pickupLocation" className="block text-gray-700 font-medium mb-2">
                          Pickup Location *
                        </label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            id="pickupLocation"
                            name="pickupLocation"
                            value={formData.pickupLocation}
                            onChange={handleChange}
                            required
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                            placeholder="Enter full address (e.g., 123 Paradise Island Dr, Nassau)"
                          />
                        </div>
                        <p className="text-xs text-gray-600 mt-2 italic">
                          Please enter the complete patient pickup address including street name and area
                        </p>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <p className="text-sm text-gray-700">
                          <strong className="text-gray-900">Destination:</strong> Bahamas Retina and Eye Care Services, Island Ln, Nassau, Bahamas (Fixed)
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="appointmentDate" className="block text-gray-700 font-medium mb-2">
                            Appointment Date *
                          </label>
                          <div className="relative">
                            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="date"
                              id="appointmentDate"
                              name="appointmentDate"
                              value={formData.appointmentDate}
                              onChange={handleChange}
                              required
                              min={new Date().toISOString().split('T')[0]}
                              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                            />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="appointmentTime" className="block text-gray-700 font-medium mb-2">
                            Appointment Time *
                          </label>
                          <div className="relative">
                            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="time"
                              id="appointmentTime"
                              name="appointmentTime"
                              value={formData.appointmentTime}
                              onChange={handleChange}
                              required
                              className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="officeContact" className="block text-gray-700 font-medium mb-2">
                          Office Contact Person *
                        </label>
                        <div className="relative">
                          <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <input
                            type="text"
                            id="officeContact"
                            name="officeContact"
                            value={formData.officeContact}
                            onChange={handleChange}
                            required
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
                            placeholder="Sun Med staff name"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Special Instructions */}
                  <div className="border-t border-cyan-100 pt-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <MessageSquare className="w-6 h-6 text-cyan-600 mr-3" />
                      Notes / Special Instructions (Optional)
                    </h2>
                    <textarea
                      id="specialInstructions"
                      name="specialInstructions"
                      value={formData.specialInstructions}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300 resize-none"
                      placeholder="Any special requirements or instructions for the driver (e.g., wheelchair assistance, walker, mobility concerns)"
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-5 rounded-lg font-bold text-xl hover:from-cyan-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
                  </button>
                </form>
              </div>
            </div>

            {/* Fixed Rate Details Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-8 rounded-3xl shadow-xl text-white sticky top-32">
                <div className="flex items-center space-x-3 mb-6">
                  <DollarSign className="w-8 h-8" />
                  <h2 className="text-2xl font-bold">Fixed Contract Rate</h2>                </div>

                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl mb-6">
                  <p className="text-cyan-100 text-sm mb-1">Fixed Rate</p>
                  <p className="text-5xl font-bold">$125.00</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <p className="text-cyan-50 text-sm">Round-trip transport included</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <p className="text-cyan-50 text-sm">1 hour waiting time at appointment</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <p className="text-cyan-50 text-sm">1 passenger</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <p className="text-cyan-50 text-sm">Fixed destination: Bahamas Retina and Eye Care Services</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-300 flex-shrink-0 mt-0.5" />
                    <p className="text-cyan-50 text-sm">Return to same pickup location</p>
                  </div>
                </div>

                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl">
                  <p className="text-cyan-100 text-xs mb-2">
                    <strong>Service Type:</strong> Medical Round-Trip
                  </p>
                  <p className="text-cyan-100 text-xs mb-2">
                    <strong>Client Type:</strong> Business Partner
                  </p>
                  <p className="text-cyan-100 text-xs">
                    <strong>Rate Type:</strong> Fixed Contract Rate
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SunMedBooking;
